import React, { useRef } from 'react';
import { useNavigateAndScroll } from '../../Hooks/useNavigateAndScroll';

const OfficePolicies = () => {
  const barRef = useRef(null);
  const navigateAndScroll = useNavigateAndScroll();
  const infoCardsData = {
    newPatient: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/New+Pt+registration.webp',
      infoTitle: 'New Patient Registration',
      infoText: 'Quickly register online for your first visit. Fill out personal, insurance, and health history details to get started with our practice in just a few minutes.',
      path: '/patient-center/new-patient'
    },
    patientForms: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Patient+forms.webp',
      infoTitle: 'Patient Forms',
      infoText: 'Access all necessary intake and consent forms to streamline your visit. Download, complete, and upload them securely before your appointment.',
      path: '/patient-center/patient-forms'
    },
    refillRequests: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Med+refill.webp',
      infoTitle: 'Refill Requests',
      infoText: 'Submit medication refill requests online anytime. Our team will process them promptly and notify you when they’re ready for pickup or sent to your pharmacy.',
      path: '/patient-center/refill-requests'
    },
    officePolicies: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Office+Policy.webp',
      infoTitle: 'Office Policies',
      infoText: 'Review our appointment, cancellation, and billing policies to ensure a smooth experience. Everything you need to know about visits, payments, and patient responsibilities.',
      path: '/patient-center/office-policies'
    },
    faqs: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/FAQ.webp',
      infoTitle: "FAQ's",
      infoText: 'Find quick answers to the most common questions—hours of operation, new patient procedures, after-hours care, and more—all in one place.',
      path: '/patient-center/faqs'
    }
  };
  return (
    <div>
    <div className="relative w-auto z-1 mt-[-70px]">
          <img
            className="w-full h-auto rounded-lg shadow-lg mt-2"
            src="https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/office+policy_InPixio.webp"
            alt="Mother and child meeting a doctor"
          />
        </div>
    <div ref={barRef} className="sticky top-0 flex justify-around bg-custom-blue text-white z-50">
        {Object.values(infoCardsData).map((card) => (
          <button
            key={card.infoTitle}
            className="p-1 text-sm cursor-pointer grow-text"
            onClick={() => navigateAndScroll(card.path, card.infoTitle)}
          >
            {card.infoTitle}
          </button>
        ))}
      </div>
    <div className="w-full px-4 py-8 sm:px-6 lg:px-64">
      {/* Title */}
      <h2 className="text-4xl font-bold text-gray-800 mb-2">Office Policies</h2>
      <hr className="border-t-2 border-blue-500 mb-6" />

      <div className="space-y-6 text-gray-600">
        {/* Co-Payments */}
        <section>
          <p className="font-bold text-gray-800 mb-1">Co-Payments</p>
          <p className="mb-4">
            Patients are required to present an insurance card at each visit. All co-payments and outstanding balances are due and payable at the time of service.
          </p>
        </section>

        {/* Self-Pay Accounts */}
        <section>
          <p className="font-bold text-gray-800 mb-1">Self-Pay Accounts</p>
          <p className="mb-4">
            Self-pay accounts are defined as patients covered by insurance plans with which Central Illinois Pediatric Associates does not participate, patients who do not have an insurance card on file, or patients without insurance coverage. Payment in full is expected at the time of service.
          </p>
        </section>

        {/* Non-Participating Insurance Plans */}
        <section>
          <p className="font-bold text-gray-800 mb-1">Non-Participating Insurance Plans</p>
          <p className="mb-4">
            Central Illinois Pediatric Associates will submit claims to non-participating insurance plans as a courtesy to our patients. Payment in full is required at the time of service, and the insurance company will reimburse the parent directly. If the insurance payment is received by Central Illinois Pediatric Associates, it will be endorsed and forwarded to the parent, provided the account balance meets the necessary criteria.
          </p>
        </section>

        {/* Patient Refunds */}
        <section>
          <p className="font-bold text-gray-800 mb-1">Patient Refunds</p>
          <p className="mb-4">
            Patient refunds will only be issued under the following conditions: there are no outstanding insurance claims or patient balances on the account.
          </p>
        </section>

        {/* Payment Plan Agreements */}
        <section>
          <p className="font-bold text-gray-800 mb-1">Payment Plan Agreements</p>
          <p className="mb-4">
            Central Illinois Pediatric Associates may offer payment plan agreements to patients experiencing financial hardship. Each agreement is tailored to the individual family's situation and can be arranged through the billing office. Please contact us if you require special arrangements.
          </p>
        </section>

        {/* Child Custody Cases */}
        <section>
          <p className="font-bold text-gray-800 mb-1">Child Custody Cases</p>
          <p className="mb-4">
            The parent with primary custody, typically the parent who the child resides with and who regularly brings the child for care, is responsible for co-payments at the time of service and for any past due balances. If the non-custodial parent carries the insurance, Central Illinois Pediatric Associates will bill the insurance company directly. Central Illinois Pediatric Associates does not participate in divorce settlements or financial arrangements (e.g., splitting the payment between parents). It is the responsibility of the parents to make payment arrangements and ensure timely payment to Central Illinois Pediatric Associates.
          </p>
        </section>

        {/* Referrals */}
        <section>
          <p className="font-bold text-gray-800 mb-1">Referrals</p>
          <p className="mb-4">
            If your insurance plan requires you to designate a primary care physician (PCP), authorization from us, your PCP, is necessary prior to seeing a specialist. Routine referrals require 5-7 business days to process. Emergency referrals will be handled on a case-by-case basis. Do not contact our office from a specialist's office unless you have already obtained a referral. In the absence of a referral, you may choose to reschedule your appointment or self-refer, and pay specialist fees at the time of service.
          </p>
        </section>

        {/* Closing Note */}
        <section>
          <p className="mb-4">
            This financial policy ensures that we can continue to provide high-quality care to our patients. If you have any questions or need further clarification regarding any of the above policies, please do not hesitate to contact us.
          </p>
        </section>
      </div>
    </div>
    </div>
  );
};

export default OfficePolicies;